import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ElliottFace from '../../../../components/ElliottFace';
import DosenButton from '../../../../../../components/ui/Buttons/DosenButton';
import type { RecommendedCourse } from './knowledgeGaps';

interface Props {
  course: RecommendedCourse;
  // Opens Add New Program prefilled from the recommendation.
  onPublish: (course: RecommendedCourse) => void;
  // Ignores the recommendation — same as Ignore on it in the Learning Gaps panel.
  onIgnore?: () => void;
  // Matches the collapsed program card height so the column stays even.
  height?: number;
}

// Hover content for the clipped description — every requirement the
// recommendation carries into the Add New Program flow.
const RequirementsTooltip: React.FC<{ course: RecommendedCourse }> = ({ course }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', py: '4px' }}>
    {(
      [
        ['Name', course.name],
        ['Audience', course.audiences.join(', ')],
        ['Description', course.purpose],
        ['Tone', course.tone.join(', ')],
        ['Informed by', course.documents.join(', ')]
      ] as const
    ).map(([label, value]) => (
      <Box key={label} sx={{ display: 'flex', gap: '6px' }}>
        <Typography sx={{ flexShrink: 0, fontSize: 11, color: '#868686', width: 74 }}>{label}:</Typography>
        <Typography sx={{ fontSize: 11, color: '#FDFDFD', lineHeight: '16px' }}>{value || '—'}</Typography>
      </Box>
    ))}
  </Box>
);

/**
 * A course Elliott has recommended off the back of a gap, pinned to the top of
 * the pillar the originating lesson's program lives in (or his best-fit pillar
 * for a gap built purely from learner queries).
 *
 * Deliberately lighter than a real ProgramCard — dashed border, no status or
 * completion — so it never reads as an existing program.
 */
const RecommendedCourseCard: React.FC<Props> = ({ course, onPublish, onIgnore, height }) => (
  <Box
    sx={{
      flexShrink: 0,
      // Exactly the collapsed program card height so the two sit level.
      height,
      border: '1px dashed #F79420',
      borderRadius: '6px',
      bgcolor: 'rgba(247, 148, 32, 0.06)',
      p: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      boxSizing: 'border-box'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <ElliottFace size={16} />
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#F79420'
        }}
      >
        Recommended Course
      </Typography>
    </Box>

    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        color: '#FDFDFD',
        lineHeight: '18px'
      }}
    >
      {course.name}
    </Typography>

    {/* Description clipped to two lines; hovering shows every requirement the
        recommendation carries into the creation flow. */}
    <Tooltip
      title={<RequirementsTooltip course={course} />}
      placement="right"
      componentsProps={{ tooltip: { sx: { maxWidth: 340 } } }}
    >
      <Typography
        sx={{
          flex: 1,
          minHeight: 0,
          cursor: 'default',
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          color: '#868686',
          lineHeight: '16px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {course.purpose}
      </Typography>
    </Tooltip>

    {/* One row, same height as the full-width CTA it replaces, so the card
        keeps its size: Review Course takes the room Ignore leaves. */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
      <DosenButton
        tone="cta"
        sizeVariant="xs"
        onClick={() => onPublish(course)}
        sx={{ flex: 1, minWidth: 0, py: '7px', whiteSpace: 'nowrap' }}
      >
        Review Course
      </DosenButton>
      {onIgnore && (
        <Box
          component="button"
          type="button"
          onClick={onIgnore}
          sx={{
            flexShrink: 0,
            border: 'none',
            bgcolor: 'transparent',
            p: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: '#868686',
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': { color: '#FDFDFD' }
          }}
        >
          Ignore
        </Box>
      )}
    </Box>
  </Box>
);

export default RecommendedCourseCard;
